import classNames from 'clsx';
import React, { type ReactElement, memo } from 'react';
import { useSelector } from 'react-redux';
import { referralProgramHelpLink, referralProgramTariffs } from 'reactApp/appHelpers/featuresHelpers/features/referralProgram';
import { vkMusicBundleLicenseLink, vkMusicBundleTariffList } from 'reactApp/appHelpers/featuresHelpers/features/vkMusicBundle';
import { AUTOUPLOAD_10TB_REGEX } from 'reactApp/modules/products/products.helpers';
import { ReferralProgramSelectors } from 'reactApp/modules/referralProgram/referralProgram.selectors';
import type { Product } from 'reactApp/types/Billing';
import { ButtonLink } from 'reactApp/ui/ButtonLink/ButtonLink';
import { useProduct } from 'reactApp/ui/PaymentFooter/useProduct';

import styles from './PaymentFooter.css';

type Props = {
    productId: string;
    isMobile?: boolean;
    product?: Product;
};

export const PaymentFooter = memo<Props>(({ productId, isMobile, product }): ReactElement => {
    const { text, isMbiz, isOneTime = false } = useProduct({ productId, product });
    const hasRefId = useSelector(ReferralProgramSelectors.getRefId);
    const isReferralProduct =
        hasRefId && productId && (referralProgramTariffs?.month?.includes(productId) || referralProgramTariffs?.year?.includes(productId));
    const isVkMusicBundleProduct = productId && vkMusicBundleTariffList.includes(productId);

    if (isOneTime) {
        return (
            <div className={classNames(styles.root, { [styles.root_mobile]: isMobile })}>
                {text} Оплачивая услугу, вы принимаете{' '}
                <ButtonLink size="medium" primary inline href="/LA/" target="_blank" fontInherit underline={false}>
                    Условия использования
                </ButtonLink>{' '}
                и предоставляете нам право единоразово списать её стоимость с указанной карты
            </div>
        );
    }

    if (isMbiz) {
        return (
            <div className={classNames(styles.root, { [styles.root_mobile]: isMobile })}>
                {text} Оформляя её, вы принимаете{' '}
                <ButtonLink size="medium" primary inline href="/LA/" target="_blank" fontInherit underline={false}>
                    Условия использования
                </ButtonLink>
                .
            </div>
        );
    }

    const referralStringNode = (
        <>
            и соглашаетесь с&nbsp;
            <ButtonLink
                className={styles.inline}
                size="medium"
                primary
                inline
                href={referralProgramHelpLink}
                target="_blank"
                fontInherit
                underline={false}
            >
                правилами реферальной программы
            </ButtonLink>
            ,
        </>
    );

    const vkMusicBundleStringNode = (
        <>
            При оплате вы принимаете условия Правил предоставления специального предложения&nbsp;
            <ButtonLink
                className={styles.inline}
                size="medium"
                primary
                inline
                href={vkMusicBundleLicenseLink}
                target="_blank"
                fontInherit
                underline={false}
            >
                «Подписка Mail Space и VK Музыка»
            </ButtonLink>
            , а также специальных документов сервисов Облако Mail и VK Музыка, указанных в Правилах.
        </>
    );

    return (
        <div className={classNames(styles.root, { [styles.root_mobile]: isMobile })}>
            {text} {isVkMusicBundleProduct && vkMusicBundleStringNode} Оформляя её, вы принимаете{' '}
            <ButtonLink size="medium" primary inline href="/LA/" target="_blank" fontInherit underline={false}>
                Условия использования
            </ButtonLink>{' '}
            {isReferralProduct ? referralStringNode : 'и'}
            &nbsp;предоставляете нам право взимать плату на&nbsp;регулярной основе. Отменить автоплатёж можно в&nbsp;любое время
            в&nbsp;настройках подписки. C&nbsp;привязанной карты будет списываться автоплатеж для всех активных подписок, приобретаемых
            на&nbsp;сайте.
            {/* tempexp_16481-next-line */}
            {AUTOUPLOAD_10TB_REGEX.test(productId) && (
                <>
                    {' '}
                    Акция «+10 ТБ для телефона» ограничена во времени — смотрите{' '}
                    <ButtonLink
                        size="medium"
                        primary
                        inline
                        href="https://promo-rules.hb.ru-msk.vkcs.cloud/10-tb-rules.pdf"
                        target="_blank"
                        fontInherit
                        underline={false}
                    >
                        условия акции
                    </ButtonLink>
                </>
            )}
        </div>
    );
});

PaymentFooter.displayName = 'PaymentFooter';
